import { useState, useRef, useEffect } from "react";
import { useAgent } from "@/contexts/AgentContext";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { FeedbackButtons } from "./FeedbackButtons";
import {
  Bot,
  X,
  Send,
  Loader2,
  Minimize2,
  Maximize2,
} from "lucide-react";
import { cn } from "@/lib/utils";
import ReactMarkdown from "react-markdown";
import { StrataLogo } from "@/components/StrataLogo";

interface Message {
  id: string;
  role: "user" | "assistant";
  content: string;
  interactionId?: string;
  actions?: Array<{ type: string; description: string }>;
}

export function AgentAssistant() {
  const { user } = useAuth();
  const { think, isThinking, logInteraction, recordFeedback } = useAgent();
  const [isOpen, setIsOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [messages, setMessages] = useState<Message[]>([
    {
      id: "welcome",
      role: "assistant",
      content: "Hi! I'm your AI assistant. I can help you create tables, suggest fields, build automations, and more. What would you like to do?",
    },
  ]);
  const [input, setInput] = useState("");
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Auto-scroll on new messages
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  // Focus input when opened
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim() || isThinking) return;

    const userMessage = input.trim();
    setInput("");

    // Add user message
    const userMsgId = crypto.randomUUID();
    setMessages((prev) => [
      ...prev,
      { id: userMsgId, role: "user", content: userMessage },
    ]);

    // Get AI response
    const startTime = Date.now();
    const response = await think(userMessage);
    const duration = Date.now() - startTime;

    // Log interaction
    const interactionId = await logInteraction(
      "agent_chat",
      { message: userMessage },
      { response: response?.response, actions: response?.actions },
      response ? "success" : "failed",
      duration
    );

    // Add assistant message
    const assistantMsgId = crypto.randomUUID();
    setMessages((prev) => [
      ...prev,
      {
        id: assistantMsgId,
        role: "assistant",
        content: response?.response || "Sorry, I couldn't process that request. Please try again.",
        interactionId: interactionId || undefined,
        actions: response?.actions,
      },
    ]);
  };

  const handleFeedback = async (messageId: string, feedback: "positive" | "negative") => {
    const message = messages.find((m) => m.id === messageId);
    if (message?.interactionId) {
      await recordFeedback(message.interactionId, feedback);
    }
  };

  // Only show for authenticated users
  if (!user) {
    return null;
  }

  if (!isOpen) {
    return (
      <Button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-20 md:bottom-6 right-4 md:right-6 h-14 w-14 rounded-full shadow-lg z-50"
        size="icon"
      >
        <StrataLogo size="lg" />
        <span className="sr-only">Open AI Assistant</span>
      </Button>
    );
  }

  return (
    <div
      className={cn(
        "fixed z-50 bg-background border rounded-lg shadow-2xl flex flex-col transition-all duration-200",
        isExpanded
          ? "inset-4 md:inset-8"
          : "bottom-20 md:bottom-6 right-4 md:right-6 w-[calc(100vw-2rem)] md:w-96 h-[500px] max-h-[80vh]"
      )}
    >
      {/* Header */}
      <div className="flex items-center justify-between p-3 border-b bg-muted/50">
        <div className="flex items-center gap-2">
          <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
            <Bot className="h-4 w-4 text-primary" />
          </div>
          <div>
            <h3 className="font-semibold text-sm">AI Assistant</h3>
            <p className="text-xs text-muted-foreground">
              {isThinking ? "Thinking..." : "Ask me anything"}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={() => setIsExpanded(!isExpanded)}
          >
            {isExpanded ? (
              <Minimize2 className="h-4 w-4" />
            ) : (
              <Maximize2 className="h-4 w-4" />
            )}
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={() => setIsOpen(false)}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Messages */}
      <ScrollArea className="flex-1 p-4" ref={scrollRef}>
        <div className="space-y-4">
          {messages.map((message) => (
            <div
              key={message.id}
              className={cn(
                "flex",
                message.role === "user" ? "justify-end" : "justify-start"
              )}
            >
              <div
                className={cn(
                  "max-w-[85%] rounded-lg px-3 py-2",
                  message.role === "user"
                    ? "bg-primary text-primary-foreground"
                    : "bg-muted"
                )}
              >
                {message.role === "assistant" ? (
                  <div className="prose prose-sm dark:prose-invert max-w-none">
                    <ReactMarkdown>{message.content}</ReactMarkdown>
                  </div>
                ) : (
                  <p className="text-sm">{message.content}</p>
                )}

                {/* Actions suggested */}
                {message.actions && message.actions.length > 0 && (
                  <div className="mt-2 pt-2 border-t border-border/50 space-y-1">
                    <p className="text-xs font-medium text-muted-foreground">
                      Suggested actions:
                    </p>
                    {message.actions.map((action, i) => (
                      <div
                        key={i}
                        className="text-xs bg-background/50 rounded px-2 py-1"
                      >
                        <span className="font-medium">{action.type}:</span>{" "}
                        {action.description}
                      </div>
                    ))}
                  </div>
                )}

                {/* Feedback buttons for assistant messages */}
                {message.role === "assistant" && message.id !== "welcome" && (
                  <div className="mt-2 flex justify-end">
                    <FeedbackButtons
                      onPositive={() => handleFeedback(message.id, "positive")}
                      onNegative={() => handleFeedback(message.id, "negative")}
                    />
                  </div>
                )}
              </div>
            </div>
          ))}

          {/* Thinking indicator */}
          {isThinking && (
            <div className="flex justify-start">
              <div className="bg-muted rounded-lg px-3 py-2 flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span className="text-sm text-muted-foreground">Thinking...</span>
              </div>
            </div>
          )}
        </div>
      </ScrollArea>

      {/* Input */}
      <form onSubmit={handleSubmit} className="p-3 border-t">
        <div className="flex gap-2">
          <Input
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Ask me anything..."
            disabled={isThinking}
            className="flex-1"
          />
          <Button type="submit" size="icon" disabled={isThinking || !input.trim()}>
            {isThinking ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Send className="h-4 w-4" />
            )}
          </Button>
        </div>
      </form>
    </div>
  );
}
